import React, { useState, useEffect, useCallback } from 'react';
import { retirementScenariosAPI } from '../../../services/api';

const COLOURS = {
  primary: '#072040',
  secondary: '#f0a500',
  success: '#91CF9B',
  info: '#599BE3',
  light: '#E7E6E6',
  white: '#ffffff',
  danger: '#ffc107',
};

// Format currency in compact form (e.g., $1.2M, $75k)
const formatCompactCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  if (num >= 1000000) {
    return `$${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `$${(num / 1000).toFixed(0)}k`;
  }
  return `$${num.toFixed(0)}`;
};

// Format full currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

// Format percentage
const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  // If value is already 0-100 range (success probability)
  if (num > 1) {
    return `${num.toFixed(decimals)}%`;
  }
  // If value is decimal (returns, volatility)
  return `${(num * 100).toFixed(decimals)}%`;
};

// Format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// Drawdown Details Modal
const DrawdownModal = ({ show, onClose, details }) => {
  if (!show || !details) return null;

  const getMethodLabel = (method) => {
    switch (method) {
      case '$': return 'Fixed Dollar';
      case '%': return 'Fixed Percentage';
      default: return 'Dynamic';
    }
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: COLOURS.primary, color: COLOURS.white }}>
            <h6 className="modal-title">
              <i className="bi bi-cash-stack me-2"></i>
              Drawdown Details
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <table className="table table-sm mb-3">
              <tbody>
                <tr>
                  <td className="text-muted">Method</td>
                  <td className="fw-bold">{getMethodLabel(details.method)}</td>
                </tr>
                <tr>
                  <td className="text-muted">
                    {details.method === '%' ? 'Drawdown Rate' : 'Drawdown Amount'}
                  </td>
                  <td className="fw-bold">
                    {details.method === '%'
                      ? formatPercent(details.input_value)
                      : formatCurrency(details.input_value)}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Projection Years</td>
                  <td className="fw-bold">{details.years}</td>
                </tr>
                <tr>
                  <td className="text-muted">Use SIS Minimum</td>
                  <td className="fw-bold">{details.use_sis_minimum ? 'Yes' : 'No'}</td>
                </tr>
                <tr>
                  <td className="text-muted">Desired Bequest</td>
                  <td className="fw-bold">{formatCurrency(details.desired_bequest)}</td>
                </tr>
              </tbody>
            </table>

            {/* Step-Down Withdrawals */}
            {details.step_down_withdrawals && details.step_down_withdrawals.length > 0 && (
              <>
                <h6 className="mt-3 mb-2" style={{ color: COLOURS.primary }}>
                  <i className="bi bi-graph-down me-2"></i>
                  Step-Down Withdrawals
                </h6>
                <table className="table table-sm table-bordered">
                  <thead style={{ backgroundColor: COLOURS.light }}>
                    <tr>
                      <th>From Age</th>
                      <th>To Age</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.step_down_withdrawals.map((step, idx) => (
                      <tr key={idx}>
                        <td>{step.fromAge}</td>
                        <td>{step.toAge}</td>
                        <td>{formatCurrency(step.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Lump Sum Withdrawals */}
            {details.lump_sum_withdrawals && details.lump_sum_withdrawals.length > 0 && (
              <>
                <h6 className="mt-3 mb-2" style={{ color: COLOURS.primary }}>
                  <i className="bi bi-box-arrow-up me-2"></i>
                  Lump Sum Withdrawals
                </h6>
                <table className="table table-sm table-bordered">
                  <thead style={{ backgroundColor: COLOURS.light }}>
                    <tr>
                      <th>Age</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.lump_sum_withdrawals.map((lump, idx) => (
                      <tr key={idx}>
                        <td>{lump.age}</td>
                        <td>{formatCurrency(lump.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Portfolio Details Modal
const PortfolioModal = ({ show, onClose, details }) => {
  if (!show || !details) return null;

  const getAnnuityTypeLabel = (type) => {
    switch (type) {
      case 'inflation_adjusted': return 'Inflation Adjusted';
      case 'fixed': return 'Fixed';
      case 'increasing': return 'Increasing';
      default: return type || '-';
    }
  };

  // Filter out zero-weight portfolios
  const activeWeights = Object.entries(details.weights || {})
    .filter(([_, weight]) => weight > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: COLOURS.primary, color: COLOURS.white }}>
            <h6 className="modal-title">
              <i className="bi bi-pie-chart me-2"></i>
              Portfolio Details
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {/* Portfolio Summary */}
            <table className="table table-sm mb-3">
              <tbody>
                <tr>
                  <td className="text-muted">Growth Exposure</td>
                  <td className="fw-bold">{formatPercent(details.growth_exposure, 0)}</td>
                </tr>
                <tr>
                  <td className="text-muted">Expected Return (after tax)</td>
                  <td className="fw-bold">{formatPercent(details.calculated_return)}</td>
                </tr>
                <tr>
                  <td className="text-muted">Volatility</td>
                  <td className="fw-bold">{formatPercent(details.volatility)}</td>
                </tr>
              </tbody>
            </table>

            {/* Portfolio Allocation */}
            {activeWeights.length > 0 && (
              <>
                <h6 className="mt-3 mb-2" style={{ color: COLOURS.primary }}>
                  <i className="bi bi-bar-chart me-2"></i>
                  Portfolio Allocation
                </h6>
                <table className="table table-sm table-bordered">
                  <thead style={{ backgroundColor: COLOURS.light }}>
                    <tr>
                      <th>Portfolio</th>
                      <th className="text-end">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeWeights.map(([name, weight]) => (
                      <tr key={name}>
                        <td>{name}</td>
                        <td className="text-end">{formatPercent(weight, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Annuity Details */}
            {details.income_stream > 0 && (
              <>
                <h6 className="mt-3 mb-2" style={{ color: COLOURS.primary }}>
                  <i className="bi bi-calendar-check me-2"></i>
                  Annuity / Income Stream
                </h6>
                <table className="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted">Annual Income</td>
                      <td className="fw-bold">{formatCurrency(details.income_stream)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Purchase Amount</td>
                      <td className="fw-bold">{formatCurrency(details.income_stream_asset_test)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Type</td>
                      <td className="fw-bold">{getAnnuityTypeLabel(details.annuity_type)}</td>
                    </tr>
                    {details.annuity_commencement_age && (
                      <tr>
                        <td className="text-muted">Commencement Age</td>
                        <td className="fw-bold">{details.annuity_commencement_age}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Income Breakdown Modal
const IncomeModal = ({ show, onClose, breakdown }) => {
  if (!show || !breakdown) return null;

  const incomeItems = [
    { label: 'Portfolio Income', value: breakdown.portfolio_income, color: COLOURS.primary },
    { label: 'Age Pension', value: breakdown.pension_income, color: COLOURS.success },
    { label: 'Employment Income', value: breakdown.employment_income, color: COLOURS.info },
    { label: 'Other Income', value: breakdown.other_income, color: COLOURS.secondary },
    { label: 'Rental Income', value: breakdown.rental_income, color: '#8884d8' },
    { label: 'Annuity Income', value: breakdown.annuity_income, color: '#82ca9d' },
  ].filter(item => item.value > 0);

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: COLOURS.primary, color: COLOURS.white }}>
            <h6 className="modal-title">
              <i className="bi bi-currency-dollar me-2"></i>
              Average Income Breakdown (50th Percentile)
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <table className="table table-sm">
              <tbody>
                {incomeItems.map((item) => (
                  <tr key={item.label}>
                    <td>
                      <span
                        className="d-inline-block me-2"
                        style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: item.color,
                          borderRadius: '2px'
                        }}
                      ></span>
                      {item.label}
                    </td>
                    <td className="text-end fw-bold">{formatCurrency(item.value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid ' + COLOURS.primary }}>
                  <td className="fw-bold">Total Average Income</td>
                  <td className="text-end fw-bold" style={{ color: COLOURS.success }}>
                    {formatCurrency(breakdown.total_income)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Clickable cell component with obvious indicator
const ClickableCell = ({ children, onClick, title, hasData = true }) => (
  <td
    className="text-end clickable-cell"
    onClick={onClick}
    style={{
      cursor: 'pointer',
      backgroundColor: hasData ? 'rgba(89, 155, 227, 0.08)' : 'transparent',
      borderLeft: hasData ? '2px solid #599BE3' : 'none',
      transition: 'background-color 0.15s ease',
    }}
    title={title || 'Click for details'}
    onMouseEnter={(e) => {
      if (hasData) e.currentTarget.style.backgroundColor = 'rgba(89, 155, 227, 0.18)';
    }}
    onMouseLeave={(e) => {
      if (hasData) e.currentTarget.style.backgroundColor = 'rgba(89, 155, 227, 0.08)';
    }}
  >
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      {children}
      {hasData && (
        <i
          className="bi bi-info-circle-fill"
          style={{ fontSize: '0.8rem', color: '#599BE3' }}
        ></i>
      )}
    </span>
  </td>
);

// Client row component with expand/collapse
const ClientRow = ({ client, isExpanded, onToggle, onSelectScenario, selectedScenarioId, isLoading, onDeleteScenario, onDeleteClient }) => {
  const [scenarios, setScenarios] = useState([]);
  const [isLoadingScenarios, setIsLoadingScenarios] = useState(false);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Modal states
  const [drawdownModal, setDrawdownModal] = useState({ show: false, details: null });
  const [portfolioModal, setPortfolioModal] = useState({ show: false, details: null });
  const [incomeModal, setIncomeModal] = useState({ show: false, breakdown: null });

  // Fetch scenarios when expanded
  useEffect(() => {
    if (isExpanded && scenarios.length === 0) {
      const fetchScenarios = async () => {
        setIsLoadingScenarios(true);
        setError(null);
        try {
          const response = await retirementScenariosAPI.getClientScenariosDetails(client.client_identifier);
          setScenarios(response.scenarios);
        } catch (err) {
          setError(err.response?.data?.detail || 'Failed to load scenarios');
        } finally {
          setIsLoadingScenarios(false);
        }
      };
      fetchScenarios();
    }
  }, [isExpanded, client.client_identifier, scenarios.length]);

  const handleCellClick = (e, modalType, data) => {
    e.stopPropagation();
    if (modalType === 'drawdown') {
      setDrawdownModal({ show: true, details: data });
    } else if (modalType === 'portfolio') {
      setPortfolioModal({ show: true, details: data });
    } else if (modalType === 'income') {
      setIncomeModal({ show: true, breakdown: data });
    }
  };

  const handleDeleteScenario = async (e, scenarioId, scenarioName) => {
    e.stopPropagation();
    const displayName = scenarioName || `Scenario #${scenarioId}`;
    if (!window.confirm(`Delete "${displayName}"?\n\nThis cannot be undone.`)) return;

    setIsDeleting(true);
    try {
      await onDeleteScenario(scenarioId);
      // Remove from local state
      setScenarios(prev => prev.filter(s => s.id !== scenarioId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete scenario');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClient = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ALL scenarios for "${client.client_identifier}"?\n\nThis will delete ${client.scenario_count} scenario(s) and cannot be undone.`)) return;

    setIsDeleting(true);
    try {
      await onDeleteClient(client.client_identifier);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete client scenarios');
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Client Header Row */}
      <tr
        className="client-row"
        onClick={onToggle}
        style={{ cursor: 'pointer', backgroundColor: isExpanded ? COLOURS.light : 'transparent' }}
      >
        <td style={{ width: '30px' }}>
          <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
        </td>
        <td className="fw-bold" style={{ color: COLOURS.primary }}>
          {client.client_identifier}
        </td>
        <td className="text-muted">
          {client.scenario_count} scenario{client.scenario_count !== 1 ? 's' : ''}
        </td>
        <td className="text-muted text-end">
          {formatDate(client.latest_scenario_date)}
        </td>
        <td style={{ width: '50px' }} className="text-center">
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={handleDeleteClient}
            disabled={isDeleting}
            title={`Delete all scenarios for ${client.client_identifier}`}
            style={{ lineHeight: 1 }}
          >
            {isDeleting ? (
              <span className="spinner-border spinner-border-sm" role="status"></span>
            ) : (
              <span style={{ fontSize: '0.875rem' }}>🗑</span>
            )}
          </button>
        </td>
      </tr>

      {/* Expanded Scenarios Table */}
      {isExpanded && (
        <tr>
          <td colSpan="5" style={{ padding: 0, backgroundColor: '#f8f9fa' }}>
            <div className="p-3">
              {isLoadingScenarios ? (
                <div className="text-center py-3">
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Loading scenarios...
                </div>
              ) : error ? (
                <div className="alert alert-danger mb-0">{error}</div>
              ) : scenarios.length === 0 ? (
                <div className="text-muted text-center py-3">No scenarios found</div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0 scenario-details-table">
                      <thead>
                        <tr style={{ backgroundColor: COLOURS.primary, color: COLOURS.white }}>
                          <th>Date</th>
                          <th>Name</th>
                          <th className="text-end">Balance</th>
                          <th className="text-end">
                            Drawdown
                            <i className="bi bi-info-circle-fill ms-1" style={{ fontSize: '0.7rem', opacity: 0.7 }}></i>
                          </th>
                          <th className="text-end">
                            Portfolio
                            <i className="bi bi-info-circle-fill ms-1" style={{ fontSize: '0.7rem', opacity: 0.7 }}></i>
                          </th>
                          <th className="text-end">Success</th>
                          <th className="text-end">
                            Avg Income
                            <i className="bi bi-info-circle-fill ms-1" style={{ fontSize: '0.7rem', opacity: 0.7 }}></i>
                          </th>
                          <th className="text-end">Final Bal</th>
                          <th style={{ width: '50px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {scenarios.map((scenario) => (
                          <tr
                            key={scenario.id}
                            onClick={() => onSelectScenario(scenario)}
                            className={selectedScenarioId === scenario.id ? 'table-primary' : ''}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>{formatDate(scenario.created_at)}</td>
                            <td>{scenario.scenario_name || '-'}</td>
                            <td className="text-end">{formatCompactCurrency(scenario.starting_balance)}</td>
                            <ClickableCell
                              onClick={(e) => handleCellClick(e, 'drawdown', scenario.drawdown_details)}
                              title="Click to view drawdown details"
                              hasData={!!scenario.drawdown_details}
                            >
                              {scenario.drawdown_method}
                            </ClickableCell>
                            <ClickableCell
                              onClick={(e) => handleCellClick(e, 'portfolio', scenario.portfolio_details)}
                              title="Click to view portfolio details"
                              hasData={!!scenario.portfolio_details}
                            >
                              {formatPercent(scenario.portfolio_details?.growth_exposure, 0)} Growth
                            </ClickableCell>
                            <td className="text-end fw-bold" style={{ color: COLOURS.success }}>
                              {scenario.success_probability !== null
                                ? formatPercent(scenario.success_probability, 1)
                                : '-'}
                            </td>
                            <ClickableCell
                              onClick={(e) => handleCellClick(e, 'income', scenario.income_breakdown)}
                              title="Click to view income breakdown"
                              hasData={!!scenario.income_breakdown}
                            >
                              {formatCompactCurrency(scenario.avg_income_p50)}
                            </ClickableCell>
                            <td className="text-end">{formatCompactCurrency(scenario.final_balance_p50)}</td>
                            <td className="text-center" style={{ verticalAlign: 'middle' }}>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={(e) => handleDeleteScenario(e, scenario.id, scenario.scenario_name)}
                                disabled={isDeleting}
                                title="Delete this scenario"
                                style={{ lineHeight: 1 }}
                              >
                                <span style={{ fontSize: '0.875rem' }}>🗑</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Legend for clickable cells */}
                  <div className="mt-2 px-2 d-flex align-items-center" style={{ fontSize: '0.75rem', color: COLOURS.primary }}>
                    <i className="bi bi-info-circle-fill me-1" style={{ color: '#599BE3' }}></i>
                    <span className="text-muted">
                      Highlighted cells have additional details — click to view
                    </span>
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      )}

      {/* Modals */}
      <DrawdownModal
        show={drawdownModal.show}
        onClose={() => setDrawdownModal({ show: false, details: null })}
        details={drawdownModal.details}
      />
      <PortfolioModal
        show={portfolioModal.show}
        onClose={() => setPortfolioModal({ show: false, details: null })}
        details={portfolioModal.details}
      />
      <IncomeModal
        show={incomeModal.show}
        onClose={() => setIncomeModal({ show: false, breakdown: null })}
        breakdown={incomeModal.breakdown}
      />
    </>
  );
};

const LoadScenarioModal = ({
  show,
  onClose,
  onLoad,
  currentScenarioId,
  isLoading,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);

  const searchClients = useCallback(async (query = '') => {
    setIsSearching(true);
    setError(null);
    try {
      const response = await retirementScenariosAPI.listClients(query || null);
      setClients(response.clients);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load clients');
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      searchClients();
      setSearchQuery('');
      setSelectedScenario(null);
      setExpandedClient(null);
    }
  }, [show, searchClients]);

  const handleSearch = (e) => {
    e.preventDefault();
    searchClients(searchQuery);
  };

  const handleToggleClient = (clientIdentifier) => {
    setExpandedClient(expandedClient === clientIdentifier ? null : clientIdentifier);
    setSelectedScenario(null);
  };

  const handleSelectScenario = (scenario) => {
    setSelectedScenario(scenario);
  };

  const handleLoad = () => {
    if (selectedScenario) {
      onLoad(selectedScenario.id);
    }
  };

  const handleDeleteScenario = async (scenarioId) => {
    await retirementScenariosAPI.deleteScenario(scenarioId);
    // Update client's scenario count
    setClients(prev => prev.map(c => {
      if (c.client_identifier === expandedClient) {
        const newCount = c.scenario_count - 1;
        // If no scenarios left, remove the client
        if (newCount <= 0) return null;
        return { ...c, scenario_count: newCount };
      }
      return c;
    }).filter(Boolean));
    // Clear selection if deleted scenario was selected
    if (selectedScenario?.id === scenarioId) {
      setSelectedScenario(null);
    }
  };

  const handleDeleteClient = async (clientIdentifier) => {
    await retirementScenariosAPI.deleteClient(clientIdentifier);
    // Remove client from list
    setClients(prev => prev.filter(c => c.client_identifier !== clientIdentifier));
    // Collapse and clear selection
    if (expandedClient === clientIdentifier) {
      setExpandedClient(null);
    }
    setSelectedScenario(null);
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: COLOURS.primary, color: COLOURS.white }}>
            <h5 className="modal-title">
              <i className="bi bi-folder2-open me-2"></i>
              Saved Scenarios
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              disabled={isLoading}
            ></button>
          </div>
          <div className="modal-body">
            {/* Search Form */}
            <form onSubmit={handleSearch} className="mb-3">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by client name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button
                  type="submit"
                  className="btn btn-outline-primary"
                  disabled={isSearching}
                >
                  {isSearching ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    <span>🔍</span>
                  )}
                </button>
              </div>
            </form>

            {/* Error Display */}
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* Clients List */}
            {clients.length === 0 && !isSearching ? (
              <div className="text-center text-muted py-4">
                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                No saved scenarios found
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr style={{ backgroundColor: COLOURS.light }}>
                      <th style={{ width: '30px' }}></th>
                      <th>Client</th>
                      <th>Scenarios</th>
                      <th className="text-end">Latest</th>
                      <th style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client) => (
                      <ClientRow
                        key={client.client_identifier}
                        client={client}
                        isExpanded={expandedClient === client.client_identifier}
                        onToggle={() => handleToggleClient(client.client_identifier)}
                        onSelectScenario={handleSelectScenario}
                        selectedScenarioId={selectedScenario?.id}
                        isLoading={isLoading}
                        onDeleteScenario={handleDeleteScenario}
                        onDeleteClient={handleDeleteClient}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Selected Scenario Info */}
            {selectedScenario && (
              <div className="mt-3 p-3 border rounded" style={{ backgroundColor: COLOURS.light }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Selected:</strong>{' '}
                    {selectedScenario.scenario_name || 'Unnamed'}{' '}
                    <span className="text-muted">
                      ({formatDate(selectedScenario.created_at)})
                    </span>
                    {selectedScenario.id === currentScenarioId && (
                      <span className="badge bg-info ms-2">Currently Loaded</span>
                    )}
                  </div>
                  <div>
                    {selectedScenario.success_probability !== null && (
                      <span className="badge bg-success me-2">
                        {formatPercent(selectedScenario.success_probability)} success
                      </span>
                    )}
                    <span className="badge bg-secondary">
                      {formatCompactCurrency(selectedScenario.starting_balance)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-retirement-primary"
              onClick={handleLoad}
              disabled={!selectedScenario || isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Loading...
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-down me-2"></i>
                  Load Scenario
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadScenarioModal;
